/**
 * This file defines the connection between the web client and the trace server.
 * The intention is that the implementation is a 1-1 mapping to the trace
 * server's API. This file should not contain any business logic. If possible,
 * we could generate this from `trace_server.py`. Which in effect is a perfect
 * mapping of `weave/trace_server/trace_server_interface.py` as a web service.
 *
 * These types MUST be kept in sync with the types defined in
 * `weave/trace_server/trace_server_interface.py`. Please modify with care.
 *
 * TODO: Currently, we only implement Call Read and Call Query - there are
 * several other endpoints that we should implement.
 */

import {getCookie} from '@wandb/weave/common/util/cookie';
import {urlPrefixed} from '@wandb/weave/config';
import {HTTPError} from '@wandb/weave/errors';
import fetch from 'isomorphic-unfetch';

import {
  ActionsExecuteBatchReq,
  ActionsExecuteBatchRes,
  AgentConversationChatReq,
  AgentConversationChatRes,
  AgentSearchReq,
  AgentSearchRes,
  AgentSpansQueryReq,
  AgentSpansQueryRes,
  AgentsQueryReq,
  AgentsQueryRes,
  AgentTraceChatReq,
  AgentTraceChatRes,
  AgentVersionsQueryReq,
  AgentVersionsQueryRes,
  AnnotationQueueAddCallsBody,
  AnnotationQueueAddCallsReq,
  AnnotationQueueAddCallsRes,
  AnnotationQueueCreateReq,
  AnnotationQueueCreateRes,
  AnnotationQueueDeleteReq,
  AnnotationQueueDeleteRes,
  AnnotationQueueUpdateReq,
  AnnotationQueueUpdateRes,
  AnnotationQueueItemProgressUpdateBody,
  AnnotationQueueItemsQueryBody,
  AnnotationQueueItemsQueryReq,
  AnnotationQueueItemsQueryRes,
  AnnotationQueueReadReq,
  AnnotationQueueReadRes,
  AnnotationQueueSchema,
  AnnotationQueuesQueryReq,
  AnnotationQueuesQueryRes,
  AnnotationQueuesStatsReq,
  AnnotationQueuesStatsRes,
  AnnotatorQueueItemsProgressUpdateReq,
  AnnotatorQueueItemsProgressUpdateRes,
  CallsScoreReq,
  CallsScoreRes,
  CallsUsageReq,
  CallsUsageRes,
  CallStatsReq,
  CallStatsRes,
  CompletionsCreateReq,
  CompletionsCreateRes,
  CompletionsCreateStreamReq,
  CompletionsCreateStreamRes,
  ContentType,
  EvaluateModelReq,
  EvaluateModelRes,
  EvaluationStatusReq,
  EvaluationStatusRes,
  EvalResultsQueryReq,
  EvalResultsQueryRes,
  FeedbackCreateReq,
  FeedbackCreateRes,
  FeedbackPurgeReq,
  FeedbackPurgeRes,
  FeedbackReplaceReq,
  FeedbackReplaceRes,
  FeedbackStatsReq,
  FeedbackStatsRes,
  FeedbackPayloadSchemaReq,
  FeedbackPayloadSchemaRes,
  FeedbackQueryReq,
  FeedbackQueryRes,
  FilesStatsReq,
  FilesStatsRes,
  ImageGenerationCreateReq,
  ImageGenerationCreateRes,
  ProjectStatsReq,
  ProjectStatsRes,
  TableCreateReq,
  TableCreateRes,
  TableUpdateReq,
  TableUpdateRes,
  ThreadSchema,
  ThreadsQueryReq,
  ThreadsQueryRes,
  TraceCallReadReq,
  TraceCallReadRes,
  TraceCallSchema,
  TraceCallsDeleteReq,
  TraceCallsQueryReq,
  TraceCallsQueryRes,
  TraceCallsQueryStatsReq,
  TraceCallsQueryStatsRes,
  TraceCallUpdateReq,
  TraceFileContentReadReq,
  TraceFileContentReadRes,
  TraceObjAddTagsReq,
  TraceObjAddTagsRes,
  TraceObjCreateReq,
  TraceObjCreateRes,
  TraceObjDeleteReq,
  TraceObjDeleteRes,
  TraceObjQueryReq,
  TraceObjQueryRes,
  TraceObjReadReq,
  TraceObjReadRes,
  TraceObjRemoveAliasesReq,
  TraceObjRemoveAliasesRes,
  TraceObjRemoveTagsReq,
  TraceObjRemoveTagsRes,
  TraceObjSetAliasesReq,
  TraceObjSetAliasesRes,
  TraceTagsListReq,
  TraceTagsListRes,
  TraceAliasesListReq,
  TraceAliasesListRes,
  TraceRefsReadBatchReq,
  TraceRefsReadBatchRes,
  TraceTableQueryReq,
  TraceTableQueryRes,
  TraceTableQueryStatsBatchReq,
  TraceTableQueryStatsBatchRes,
  TraceUsageReq,
  TraceUsageRes,
} from './traceServerClientTypes';

// Maximum size in bytes for a single stream request (500 MB)
// This prevents browser crashes from excessively large payloads
const MAX_STREAM_SIZE_BYTES = 500 * 1024 * 1024;

export class DirectTraceServerClient {
  private baseUrl: string;
  private inFlightFetchesRequests: Record<
    string,
    Record<
      string,
      Array<{
        resolve: (res: any) => void;
        reject: (err: any) => void;
      }>
    >
  > = {};

  constructor(baseUrl: string) {
    this.inFlightFetchesRequests = {};
    this.baseUrl = baseUrl;
  }

  public callsDelete(req: TraceCallsDeleteReq): Promise<void> {
    return this.makeRequest<TraceCallsDeleteReq, void>('/calls/delete', req);
  }

  public callUpdate(req: TraceCallUpdateReq): Promise<void> {
    return this.makeRequest<TraceCallUpdateReq, void>('/call/update', req);
  }

  public callsQuery(req: TraceCallsQueryReq): Promise<TraceCallsQueryRes> {
    return this.makeRequest<TraceCallsQueryReq, TraceCallsQueryRes>(
      '/calls/query',
      req
    );
  }

  public callsQueryStats(
    req: TraceCallsQueryStatsReq
  ): Promise<TraceCallsQueryStatsRes> {
    return this.makeRequest<TraceCallsQueryStatsReq, TraceCallsQueryStatsRes>(
      '/calls/query_stats',
      req
    );
  }

  public evalResultsQuery(req: {
    entity: string;
    project: string;
    body: EvalResultsQueryReq;
  }): Promise<EvalResultsQueryRes> {
    return this.makeRequest<EvalResultsQueryReq, EvalResultsQueryRes>(
      `/v2/${req.entity}/${req.project}/eval_results/query`,
      req.body
    );
  }

  /**
   * Compute per-call usage for a trace, with descendant rollup.
   * Returns usage metrics for each call in the trace, where each
   * call's metrics include the sum of its own usage plus all descendants' usage.
   * Use this for trace view where you want to see rolled-up metrics per call.
   */
  public traceUsage(req: TraceUsageReq): Promise<TraceUsageRes> {
    return this.makeRequest<TraceUsageReq, TraceUsageRes>('/trace/usage', req);
  }

  /**
   * Compute aggregated usage for multiple root calls, with descendant rollup.
   * Returns usage metrics for each requested root call, where each
   * root's metrics include the sum of its own usage plus all descendants' usage.
   * Use this for trace table where you want to see rolled-up metrics per root call.
   */
  public callsUsage(req: CallsUsageReq): Promise<CallsUsageRes> {
    return this.makeRequest<CallsUsageReq, CallsUsageRes>('/calls/usage', req);
  }

  public imageCreate(
    req: ImageGenerationCreateReq
  ): Promise<ImageGenerationCreateRes> {
    try {
      return this.makeRequest<
        ImageGenerationCreateReq,
        ImageGenerationCreateRes
      >('/image/create', req);
    } catch (error: any) {
      if (error?.api_key_name) {
        console.error('Missing LLM API key:', error.api_key_name);
      }
      return Promise.reject(error);
    }
  }

  public async callsStreamQuery(
    req: TraceCallsQueryReq,
    signal?: AbortSignal
  ): Promise<TraceCallsQueryRes> {
    try {
      const response = await this.makeStreamRequestRaw(
        '/calls/stream_query',
        req,
        undefined,
        signal
      );

      if (!response.body) {
        return {calls: []};
      }

      return await this.processCallsStream(response.body, req, signal);
    } catch (err: any) {
      // Ignore abort errors - these are expected when requests are cancelled
      if (err.name === 'AbortError') {
        return {calls: []};
      }
      console.error('calls stream query error', err);
      throw err;
    }
  }
  /*
  This implementation of the calls stream query is a convenience in order
  to explicitly handle large streams of data. It should be kept in close
  sync with makeRequest.
  */

  public callsStreamDownload(
    req: TraceCallsQueryReq,
    contentType: ContentType = ContentType.any
  ): Promise<Blob> {
    const url = `${this.baseUrl}/calls/stream_query`;
    const reqBody = JSON.stringify(req);

    const headers: {[key: string]: string} = {
      'Content-Type': 'application/json',
      // This is a dummy auth header, the trace server requires
      // that we send a basic auth header, but it uses cookies for
      // authentication.
      Authorization: 'Basic ' + btoa(':'),
      Accept: contentType,
    };
    const useAdminPrivileges = getCookie('use_admin_privileges') === 'true';
    if (useAdminPrivileges) {
      headers['use-admin-privileges'] = 'true';
    }

    // TODO - somone should fix this violation
    // oxlint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        // Fetch the text data using streams
        // eslint-disable-next-line wandb/no-unprefixed-urls
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers,
          body: reqBody,
        });
        // TODO: support streaming data into a memory buffer, this .blob() method
        // is incomplete, add paging/stream construction of this blob or string. More info here:
        // https://stackoverflow.com/questions/28307789/is-there-any-limitation-on-javascript-max-blob-size
        const blob = await response.blob();
        resolve(blob);
      } catch (error) {
        reject(new Error(`Error downloading data: ${error}`));
      }
    });
  }

  public callRead(req: TraceCallReadReq): Promise<TraceCallReadRes> {
    return this.makeRequest<TraceCallReadReq, TraceCallReadRes>(
      '/call/read',
      req
    );
  }

  public objsQuery(req: TraceObjQueryReq): Promise<TraceObjQueryRes> {
    return this.makeRequest<TraceObjQueryReq, TraceObjQueryRes>(
      '/objs/query',
      req
    );
  }

  public objRead(req: TraceObjReadReq): Promise<TraceObjReadRes> {
    return this.makeRequest<TraceObjReadReq, TraceObjReadRes>('/obj/read', req);
  }

  public objDelete(req: TraceObjDeleteReq): Promise<TraceObjDeleteRes> {
    return this.makeRequest<TraceObjDeleteReq, TraceObjDeleteRes>(
      '/obj/delete',
      req
    );
  }

  public objAddTags(req: TraceObjAddTagsReq): Promise<TraceObjAddTagsRes> {
    const {object_id, digest, ...body} = req;
    return this.makeRequest<
      Omit<TraceObjAddTagsReq, 'object_id' | 'digest'>,
      TraceObjAddTagsRes
    >(`/objs/${object_id}/versions/${digest}/tags`, body, 'json', undefined, {
      method: 'PUT',
    });
  }

  public objRemoveTags(
    req: TraceObjRemoveTagsReq
  ): Promise<TraceObjRemoveTagsRes> {
    const {object_id, digest, ...body} = req;
    return this.makeRequest<
      Omit<TraceObjRemoveTagsReq, 'object_id' | 'digest'>,
      TraceObjRemoveTagsRes
    >(`/objs/${object_id}/versions/${digest}/tags/remove`, body);
  }

  public objSetAliases(
    req: TraceObjSetAliasesReq
  ): Promise<TraceObjSetAliasesRes> {
    const {object_id, ...body} = req;
    return this.makeRequest<
      Omit<TraceObjSetAliasesReq, 'object_id'>,
      TraceObjSetAliasesRes
    >(`/objs/${object_id}/aliases`, body, 'json', undefined, {
      method: 'PUT',
    });
  }

  public objRemoveAliases(
    req: TraceObjRemoveAliasesReq
  ): Promise<TraceObjRemoveAliasesRes> {
    const {object_id, ...body} = req;
    return this.makeRequest<
      Omit<TraceObjRemoveAliasesReq, 'object_id'>,
      TraceObjRemoveAliasesRes
    >(`/objs/${object_id}/aliases/remove`, body);
  }

  public objTagsList(req: TraceTagsListReq): Promise<TraceTagsListRes> {
    const {project_id} = req;
    return this.makeRequest<TraceTagsListReq, TraceTagsListRes>(
      '/tags',
      req,
      'json',
      undefined,
      {
        method: 'GET',
        queryParams: {project_id},
      }
    );
  }

  public objAliasesList(
    req: TraceAliasesListReq
  ): Promise<TraceAliasesListRes> {
    const {project_id} = req;
    return this.makeRequest<TraceAliasesListReq, TraceAliasesListRes>(
      '/aliases',
      req,
      'json',
      undefined,
      {
        method: 'GET',
        queryParams: {project_id},
      }
    );
  }

  public readBatch(req: TraceRefsReadBatchReq): Promise<TraceRefsReadBatchRes> {
    return this.makeRequest<TraceRefsReadBatchReq, TraceRefsReadBatchRes>(
      '/refs/read_batch',
      req
    );
  }

  public objCreate(req: TraceObjCreateReq): Promise<TraceObjCreateRes> {
    const initialObjectId = req.obj.object_id;
    const sanitizedObjectId = sanitizeObjectId(initialObjectId);
    if (sanitizedObjectId !== initialObjectId) {
      // Caller is expected to sanitize the object id. We should be doing this
      // on the server, but it is currently disabled.
      throw new Error(
        `Invalid object name: ${initialObjectId}, sanitized to ${sanitizedObjectId}`
      );
    }
    return this.makeRequest<TraceObjCreateReq, TraceObjCreateRes>(
      '/obj/create',
      req
    );
  }

  public tableUpdate(req: TableUpdateReq): Promise<TableUpdateRes> {
    return this.makeRequest<TableUpdateReq, TableUpdateRes>(
      '/table/update',
      req
    );
  }

  public tableCreate(req: TableCreateReq): Promise<TableCreateRes> {
    return this.makeRequest<TableCreateReq, TableCreateRes>(
      '/table/create',
      req
    );
  }

  public tableQuery(req: TraceTableQueryReq): Promise<TraceTableQueryRes> {
    return this.makeRequest<TraceTableQueryReq, TraceTableQueryRes>(
      '/table/query',
      req
    );
  }

  public tableQueryStatsBatch(
    req: TraceTableQueryStatsBatchReq
  ): Promise<TraceTableQueryStatsBatchRes> {
    return this.makeRequest<
      TraceTableQueryStatsBatchReq,
      TraceTableQueryStatsBatchRes
    >('/table/query_stats_batch', req);
  }

  public feedbackCreate(req: FeedbackCreateReq): Promise<FeedbackCreateRes> {
    return this.makeRequest<FeedbackCreateReq, FeedbackCreateRes>(
      '/feedback/create',
      req
    );
  }

  public feedbackQuery(req: FeedbackQueryReq): Promise<FeedbackQueryRes> {
    return this.makeRequest<FeedbackQueryReq, FeedbackQueryRes>(
      '/feedback/query',
      req
    );
  }

  public feedbackPurge(req: FeedbackPurgeReq): Promise<FeedbackPurgeRes> {
    return this.makeRequest<FeedbackPurgeReq, FeedbackPurgeRes>(
      '/feedback/purge',
      req
    );
  }

  public feedbackReplace(req: FeedbackReplaceReq): Promise<FeedbackReplaceRes> {
    return this.makeRequest<FeedbackReplaceReq, FeedbackReplaceRes>(
      '/feedback/replace',
      req
    );
  }

  public feedbackStats(req: FeedbackStatsReq): Promise<FeedbackStatsRes> {
    return this.makeRequest<FeedbackStatsReq, FeedbackStatsRes>(
      '/feedback/stats',
      req
    );
  }

  public feedbackPayloadSchema(
    req: FeedbackPayloadSchemaReq
  ): Promise<FeedbackPayloadSchemaRes> {
    return this.makeRequest<FeedbackPayloadSchemaReq, FeedbackPayloadSchemaRes>(
      '/feedback/payload_schema',
      req
    );
  }

  public actionsExecuteBatch(
    req: ActionsExecuteBatchReq
  ): Promise<ActionsExecuteBatchRes> {
    return this.makeRequest<ActionsExecuteBatchReq, ActionsExecuteBatchRes>(
      '/actions/execute_batch',
      req
    );
  }

  public fileContent(
    req: TraceFileContentReadReq
  ): Promise<TraceFileContentReadRes> {
    const res = this.makeRequest<TraceFileContentReadReq, ArrayBuffer>(
      '/files/content',
      req,
      'arrayBuffer'
    );
    return new Promise((resolve, reject) => {
      res
        .then(content => {
          resolve({content});
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public filesStats(req: FilesStatsReq): Promise<FilesStatsRes> {
    return this.makeRequest<FilesStatsReq, FilesStatsRes>(
      '/files/query_stats',
      req
    );
  }

  public completionsCreate(
    req: CompletionsCreateReq
  ): Promise<CompletionsCreateRes> {
    try {
      return this.makeRequest<CompletionsCreateReq, CompletionsCreateRes>(
        '/completions/create',
        req
      );
    } catch (error: any) {
      if (error?.api_key_name) {
        console.error('Missing LLM API key:', error.api_key_name);
      }
      return Promise.reject(error);
    }
  }

  public completionsCreateStream(
    req: CompletionsCreateStreamReq,
    onChunk?: (chunk: any) => void,
    signal?: AbortSignal
  ): Promise<CompletionsCreateStreamRes> {
    const url = `${this.baseUrl}/completions/create_stream`;
    const reqBody = JSON.stringify(req);

    // Set up headers with auth and admin privileges if needed
    const headers: {[key: string]: string} = {
      'Content-Type': 'application/json',
      Authorization: 'Basic ' + btoa(':'),
    };
    if (getCookie('use_admin_privileges') === 'true') {
      headers['use-admin-privileges'] = 'true';
    }

    return new Promise((resolve, reject) => {
      fetch(urlPrefixed(url), {
        method: 'POST',
        credentials: 'include',
        headers,
        body: reqBody,
        signal,
      })
        .then(response => {
          if (!response.ok || !response.body) {
            throw new Error(`HTTP ${response.status}`);
          }
          return this.processCompletionStream(response.body, onChunk);
        })
        .then(result => {
          resolve(result);
        })
        .catch(err => {
          if ((err as any)?.api_key_name) {
            console.error('Missing LLM API key:', (err as any).api_key_name);
          }
          reject(err);
        });
    });
  }

  /**
   * Process a ReadableStream of JSON lines from a calls stream.
   * Each line should be a complete TraceCallSchema JSON object.
   * Handles early termination by resuming from the last successfully parsed line.
   * @param stream The ReadableStream to process
   * @param req The original request (used for resuming on early termination)
   * @returns Promise resolving to the calls array
   */
  private processCallsStream(
    stream: ReadableStream,
    req: TraceCallsQueryReq,
    signal?: AbortSignal
  ): Promise<TraceCallsQueryRes> {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    const calls: TraceCallSchema[] = [];
    let lineIndex = 0;
    let totalBytesRead = 0;

    // Process a single line of JSON
    const processLine = (line: string) => {
      if (line.trim() === '') return;
      try {
        calls.push(JSON.parse(line));
        lineIndex++;
      } catch (err) {
        console.error(`Error parsing line ${lineIndex}:`, line, err);
        // No need to throw here, just skip the bad line.
        // throw new Error(
        //   `Failed to parse call data at line ${lineIndex}: ${err}`
        // );
      }
    };

    // Read and process the stream
    const read = (): Promise<TraceCallsQueryRes> => {
      return reader.read().then(({done, value}) => {
        if (done) {
          // Process any remaining data in the buffer
          if (buffer.trim() !== '') {
            // This is a very special case where the last line is not a
            // complete json object. This can happen if the stream is
            // terminated early. Instead of just failing, we can make a
            // new request to the server to resume the stream from the
            // last line. This should only occur in projects with massive
            // trace data (> 150MB per my own testing)
            if (lineIndex > 0) {
              const newReq = {...req};
              const origOffset = req.offset || 0;
              newReq.offset = origOffset + lineIndex;
              console.debug(
                `Early stream termination, performing a new request resuming from ${newReq.offset}`
              );
              return this.callsStreamQuery(newReq, signal).then(innerRes => {
                calls.push(...innerRes.calls);
                return {calls};
              });
            }
          }
          return {calls};
        }

        // Decode the new chunk and add it to our buffer
        buffer += decoder.decode(value, {stream: true});

        // Process complete lines from the buffer
        let newlineIndex;
        while ((newlineIndex = buffer.indexOf('\n')) >= 0) {
          const line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);
          processLine(line);
        }

        // Check if we've exceeded the size limit
        totalBytesRead += value.byteLength;
        if (totalBytesRead > MAX_STREAM_SIZE_BYTES) {
          // Cancel the reader to stop receiving data
          reader.cancel('Size limit exceeded').catch(err => {
            console.warn('Error cancelling stream reader:', err);
          });
          console.warn(
            `Stream size limit exceeded (${(totalBytesRead / (1024 * 1024)).toFixed(2)} MB). ` +
              `Stopping stream processing. Returning ${calls.length} calls.` +
              `Some results may be missing.`
          );
          return {calls};
        }

        // Continue reading
        return read();
      });
    };

    return read();
  }

  /**
   * Process a ReadableStream of JSON lines from a completion stream, handling both regular chunks and metadata.
   * @param stream The ReadableStream to process
   * @param onChunk Optional callback for each chunk as it arrives
   * @returns Promise resolving to the final chunks and weave_call_id
   */
  private processCompletionStream(
    stream: ReadableStream,
    onChunk?: (chunk: any) => void
  ): Promise<CompletionsCreateStreamRes> {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    const chunks: any[] = [];
    let weaveCallId: string | undefined;

    // Process a single line of JSON
    const processLine = (line: string) => {
      if (line.trim() === '') return;
      try {
        const parsed = JSON.parse(line);
        if (parsed._meta?.weave_call_id) {
          weaveCallId = parsed._meta.weave_call_id;
        } else {
          chunks.push(parsed);
          onChunk?.(parsed);
        }
      } catch (err) {
        console.error('Error parsing completion chunk line:', line, err);
      }
    };

    // Read and process the stream
    const read = (): Promise<void> => {
      return reader.read().then(({done, value}) => {
        if (done) {
          // Process any remaining data in the buffer
          if (buffer.trim() !== '') {
            processLine(buffer);
          }
          return;
        }

        // Decode the new chunk and add it to our buffer
        buffer += decoder.decode(value, {stream: true});

        // Process complete lines from the buffer
        let newlineIndex;
        while ((newlineIndex = buffer.indexOf('\n')) >= 0) {
          const line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);
          processLine(line);
        }

        // Continue reading
        return read();
      });
    };

    return read().then(() => ({chunks, weave_call_id: weaveCallId}));
  }

  public projectStats(req: ProjectStatsReq): Promise<ProjectStatsRes> {
    return this.makeRequest<ProjectStatsReq, ProjectStatsRes>(
      '/project/stats',
      req
    );
  }

  public async threadsStreamQuery(
    req: ThreadsQueryReq
  ): Promise<ThreadsQueryRes> {
    try {
      let content = await this.makeRequest<ThreadsQueryReq, string>(
        '/threads/stream_query',
        req,
        'text'
      );

      // `content` is jsonl string, we need to parse it.
      if (!content) {
        return {threads: []};
      }

      if (content.endsWith('\n')) {
        content = content.slice(0, -1);
      }

      if (content === '') {
        return {threads: []};
      }

      const threads: ThreadSchema[] = [];
      const lines = content.split('\n');

      for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
        const line = lines[lineIndex];
        try {
          threads.push(JSON.parse(line));
        } catch (err) {
          if (lineIndex === lines.length - 1 && lineIndex > 0) {
            // This is a very special case where the last line is not a
            // complete json object. This can happen if the stream is
            // terminated early. Instead of just failing, we can make a
            // new request to the server to resume the stream from the
            // last line. This should only occur projects with massive
            // thread data.
            const newReq = {...req};
            const origOffset = req.offset || 0;
            newReq.offset = origOffset + lineIndex;
            console.debug(
              `Early stream termination, performing a new request resuming from ${newReq.offset}`
            );

            const innerRes = await this.threadsStreamQuery(newReq);
            threads.push(...innerRes.threads);
            return {threads};
          } else {
            console.error(
              `Error parsing line ${lineIndex} of ${lines.length}: ${line}`
            );
          }
        }
      }

      return {threads};
      // TODO - somone should fix this violation
      // oxlint-disable-next-line no-useless-catch
    } catch (err) {
      throw err;
    }
  }

  // Annotation Queue API
  public annotationQueueCreate(
    req: AnnotationQueueCreateReq
  ): Promise<AnnotationQueueCreateRes> {
    return this.makeRequest<AnnotationQueueCreateReq, AnnotationQueueCreateRes>(
      '/annotation_queues',
      req
    );
  }

  public async annotationQueuesStreamQuery(
    req: AnnotationQueuesQueryReq
  ): Promise<AnnotationQueuesQueryRes> {
    try {
      let content = await this.makeRequest<AnnotationQueuesQueryReq, string>(
        '/annotation_queues/query',
        req,
        'text'
      );

      // `content` is jsonl string, we need to parse it.
      if (!content) {
        return {queues: []};
      }

      if (content.endsWith('\n')) {
        content = content.slice(0, -1);
      }

      if (content === '') {
        return {queues: []};
      }

      const queues: AnnotationQueueSchema[] = [];
      const lines = content.split('\n');

      // Note: Unlike other stream queries, we don't handle early stream termination
      // with automatic retries because annotation queues are not expected to reach
      // massive data volumes. If stream termination occurs, we simply take what's
      // available and log the error for any incomplete lines.
      for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
        const line = lines[lineIndex];
        try {
          queues.push(JSON.parse(line));
        } catch (err) {
          console.error(
            `Error parsing line ${lineIndex} of ${lines.length}: ${line}`
          );
        }
      }

      return {queues};
      // TODO: fix violation - remove unnecessary try/catch wrapper
      // eslint-disable-next-line no-useless-catch
    } catch (err) {
      throw err;
    }
  }

  public annotationQueueRead(
    req: AnnotationQueueReadReq
  ): Promise<AnnotationQueueReadRes> {
    const {queue_id, project_id} = req;
    return this.makeRequest<AnnotationQueueReadReq, AnnotationQueueReadRes>(
      `/annotation_queues/${queue_id}`,
      req,
      'json',
      undefined,
      {
        method: 'GET',
        queryParams: {project_id},
      }
    );
  }

  public annotationQueueDelete(
    req: AnnotationQueueDeleteReq
  ): Promise<AnnotationQueueDeleteRes> {
    const {queue_id, project_id} = req;
    return this.makeRequest<AnnotationQueueDeleteReq, AnnotationQueueDeleteRes>(
      `/annotation_queues/${queue_id}`,
      req,
      'json',
      undefined,
      {
        method: 'DELETE',
        queryParams: {project_id},
      }
    );
  }

  public annotationQueueUpdate(
    req: AnnotationQueueUpdateReq
  ): Promise<AnnotationQueueUpdateRes> {
    const {queue_id, ...body} = req;
    return this.makeRequest<
      Omit<AnnotationQueueUpdateReq, 'queue_id'>,
      AnnotationQueueUpdateRes
    >(`/annotation_queues/${queue_id}`, body, 'json', undefined, {
      method: 'PUT',
    });
  }

  public annotationQueueAddCalls(
    req: AnnotationQueueAddCallsReq
  ): Promise<AnnotationQueueAddCallsRes> {
    const {queue_id, ...body} = req;
    return this.makeRequest<
      AnnotationQueueAddCallsBody,
      AnnotationQueueAddCallsRes
    >(`/annotation_queues/${queue_id}/items`, body);
  }

  public annotationQueueItemsQuery(
    req: AnnotationQueueItemsQueryReq
  ): Promise<AnnotationQueueItemsQueryRes> {
    const {queue_id, ...body} = req;
    return this.makeRequest<
      AnnotationQueueItemsQueryBody,
      AnnotationQueueItemsQueryRes
    >(`/annotation_queues/${queue_id}/items/query`, body);
  }

  public annotationQueuesStats(
    req: AnnotationQueuesStatsReq
  ): Promise<AnnotationQueuesStatsRes> {
    return this.makeRequest<AnnotationQueuesStatsReq, AnnotationQueuesStatsRes>(
      '/annotation_queues/stats',
      req
    );
  }

  public annotatorQueueItemsProgressUpdate(
    req: AnnotatorQueueItemsProgressUpdateReq
  ): Promise<AnnotatorQueueItemsProgressUpdateRes> {
    const {queue_id, item_id, ...body} = req;
    return this.makeRequest<
      AnnotationQueueItemProgressUpdateBody,
      AnnotatorQueueItemsProgressUpdateRes
    >(`/annotation_queues/${queue_id}/items/${item_id}/progress`, body);
  }

  public evaluateModel(req: EvaluateModelReq): Promise<EvaluateModelRes> {
    return this.makeRequest<EvaluateModelReq, EvaluateModelRes>(
      '/evaluations/evaluate_model',
      req
    );
  }

  public evaluationStatus(
    req: EvaluationStatusReq
  ): Promise<EvaluationStatusRes> {
    return this.makeRequest<EvaluationStatusReq, EvaluationStatusRes>(
      '/evaluations/status',
      req
    );
  }

  public callsScore(req: CallsScoreReq): Promise<CallsScoreRes> {
    return this.makeRequest<CallsScoreReq, CallsScoreRes>('/calls/score', req);
  }

  public callStats(req: CallStatsReq): Promise<CallStatsRes> {
    return this.makeRequest<CallStatsReq, CallStatsRes>('/calls/stats', req);
  }

  public agentsQuery(req: AgentsQueryReq): Promise<AgentsQueryRes> {
    return this.makeRequest<AgentsQueryReq, AgentsQueryRes>(
      '/agents/query',
      req
    );
  }

  public agentSpansQuery(req: AgentSpansQueryReq): Promise<AgentSpansQueryRes> {
    return this.makeRequest<AgentSpansQueryReq, AgentSpansQueryRes>(
      '/agents/spans/query',
      req
    );
  }

  public agentVersionsQuery(
    req: AgentVersionsQueryReq
  ): Promise<AgentVersionsQueryRes> {
    return this.makeRequest<AgentVersionsQueryReq, AgentVersionsQueryRes>(
      '/agents/agent-versions/query',
      req
    );
  }

  public agentTracesChat(req: AgentTraceChatReq): Promise<AgentTraceChatRes> {
    return this.makeRequest<AgentTraceChatReq, AgentTraceChatRes>(
      '/agents/traces/chat',
      req
    );
  }

  public agentConversationChat(
    req: AgentConversationChatReq
  ): Promise<AgentConversationChatRes> {
    return this.makeRequest<AgentConversationChatReq, AgentConversationChatRes>(
      '/agents/conversations/chat',
      req
    );
  }

  public agentSearch(req: AgentSearchReq): Promise<AgentSearchRes> {
    return this.makeRequest<AgentSearchReq, AgentSearchRes>(
      '/agents/search',
      req
    );
  }

  private makeRequest = async <QT, ST>(
    endpoint: string,
    req: QT,
    responseReturnType: 'json' | 'text' | 'arrayBuffer' = 'json',
    contentType?: ContentType,
    options?: {
      method?: 'GET' | 'POST' | 'DELETE' | 'PUT';
      queryParams?: Record<string, string>;
    }
  ): Promise<ST> => {
    const method = options?.method ?? 'POST';
    let url = `${this.baseUrl}${endpoint}`;

    // Add query parameters for GET requests
    if (['GET', 'DELETE'].includes(method) && options?.queryParams) {
      const params = new URLSearchParams(options.queryParams);
      url = `${url}?${params.toString()}`;
    }

    const reqBody = JSON.stringify(req);
    let needsFetch = false;
    if (!this.inFlightFetchesRequests[endpoint]) {
      this.inFlightFetchesRequests[endpoint] = {};
    }
    if (!this.inFlightFetchesRequests[endpoint][reqBody]) {
      this.inFlightFetchesRequests[endpoint][reqBody] = [];
      needsFetch = true;
    }

    const prom = new Promise<ST>((resolve, reject) => {
      this.inFlightFetchesRequests[endpoint][reqBody].push({resolve, reject});
    });

    if (!needsFetch) {
      return prom;
    }

    const headers: {[key: string]: string} = {
      'Content-Type': 'application/json',
      // This is a dummy auth header, the trace server requires
      // that we send a basic auth header, but it uses cookies for
      // authentication.
      Authorization: 'Basic ' + btoa(':'),
      'X-Origin': window.location.origin,
    };
    const useAdminPrivileges = getCookie('use_admin_privileges') === 'true';
    if (useAdminPrivileges) {
      headers['use-admin-privileges'] = 'true';
    }

    const fetchOptions: RequestInit = {
      method,
      credentials: 'include',
      headers,
    };

    // Only add body for POST requests
    if (['POST', 'PUT'].includes(method)) {
      fetchOptions.body = reqBody;
    }

    // eslint-disable-next-line wandb/no-unprefixed-urls
    fetch(url, fetchOptions)
      .then(async response => {
        if (!response.ok) {
          throw new HTTPError(
            response.statusText,
            response.status,
            await response.text()
          );
        }
        if (responseReturnType === 'text') {
          return response.text();
        } else if (responseReturnType === 'arrayBuffer') {
          return response.arrayBuffer();
        } else if (responseReturnType === 'json') {
          return response.json();
        } else {
          // Should never happen with correct type checking
          throw new Error('Invalid responseReturnType: ' + responseReturnType);
        }
      })
      .then(res => {
        try {
          // TODO: fix violation - add nullish coalescing fallback
          const inFlightRequest = [
            // eslint-disable-next-line no-unsafe-optional-chaining
            ...this.inFlightFetchesRequests[endpoint]?.[reqBody],
          ];
          delete this.inFlightFetchesRequests[endpoint][reqBody];
          if (inFlightRequest) {
            inFlightRequest.forEach(({resolve}) => {
              resolve(res);
            });
          }
        } catch (err) {
          console.error(err);
        }
      })
      .catch(err => {
        try {
          // TODO: fix violation - add nullish coalescing fallback
          const inFlightRequest = [
            // eslint-disable-next-line no-unsafe-optional-chaining
            ...this.inFlightFetchesRequests[endpoint]?.[reqBody],
          ];
          delete this.inFlightFetchesRequests[endpoint][reqBody];
          if (inFlightRequest) {
            inFlightRequest.forEach(({reject}) => {
              reject(err);
            });
          }
        } catch (err2) {
          console.error(err2);
        }
      });

    return prom;
  };

  /**
   * Stream request handler that returns the raw Response object.
   * Used when we need to process the response body as a stream.
   * Bypasses the promise cache to ensure each streaming request is independent.
   */
  private makeStreamRequestRaw = async <QT>(
    endpoint: string,
    req: QT,
    contentType?: ContentType,
    signal?: AbortSignal
  ): Promise<Response> => {
    const url = `${this.baseUrl}${endpoint}`;
    const reqBody = JSON.stringify(req);

    const headers: {[key: string]: string} = {
      'Content-Type': 'application/json',
      // This is a dummy auth header, the trace server requires
      // that we send a basic auth header, but it uses cookies for
      // authentication.
      Authorization: 'Basic ' + btoa(':'),
    };
    if (contentType) {
      headers.Accept = contentType;
    }
    const useAdminPrivileges = getCookie('use_admin_privileges') === 'true';
    if (useAdminPrivileges) {
      headers['use-admin-privileges'] = 'true';
    }

    // eslint-disable-next-line wandb/no-unprefixed-urls
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: reqBody,
      signal,
    });

    if (!response.ok) {
      throw new HTTPError(
        response.statusText,
        response.status,
        await response.text()
      );
    }

    return response;
  };
}

/**
 * Sanitizes an object name by replacing non-alphanumeric characters with dashes and enforcing length limits.
 * This matches the Python implementation in weave_client.py.
 *
 * @param name The name to sanitize
 * @returns The sanitized name
 * @throws Error if the resulting name would be empty
 */
export function sanitizeObjectId(name: string): string {
  // Replace any non-word chars (except dots and underscores) with dashes
  let res = name.replace(/[^\w._]+/g, '-');
  // Replace multiple consecutive dashes/dots/underscores with a single dash
  res = res.replace(/([._-]{2,})+/g, '-');
  // Remove leading/trailing dashes and underscores
  res = res.replace(/^[-_]+|[-_]+$/g, '');

  if (!res) {
    throw new Error(`Invalid object name: ${name}`);
  }

  if (res.length > 128) {
    res = res.slice(0, 128);
  }

  return res;
}
